import React from 'react';
import { useTheme } from '../context/ThemeContext';
import './AuthPages.scss';

const RefundPolicyPage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`}>
      <div className="policy-page">
        <div className="policy-page__content">
          <h1 className="policy-page__title">Refund Policy</h1>
          <p className="policy-page__updated"><strong>Last Updated:</strong> January 28, 2026</p>
          
          <p className="policy-page__intro">
            At ChillVerify, we stand behind the quality of our sensor products and want to ensure our customers are completely satisfied with their purchases. This Refund Policy outlines the conditions under which refunds are issued for our products.
          </p>

          <section className="policy-page__section">
            <h2>1. 30-Day Money-Back Guarantee</h2>
            <p>
              We offer a 30-day money-back guarantee on all ChillVerify sensor products. If you are not completely satisfied with your purchase, you may request a full refund within 30 days of the delivery date.
            </p>
            <p><strong>Conditions for 30-Day Refund:</strong></p>
            <ul>
              <li>Product must be in resalable condition</li>
              <li>Original packaging must be intact and undamaged</li>
              <li>All accessories and components must be included</li>
              <li>Product must not show signs of excessive use or damage</li>
              <li>Proof of purchase is required</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>2. Digital Products and Software</h2>
            <p>
              For digital products, software licenses, and subscription services, refunds are available within 14 days of purchase, provided that:
            </p>
            <ul>
              <li>The digital product has not been extensively used</li>
              <li>No more than 25% of the service has been consumed</li>
              <li>The account is in good standing</li>
              <li>No violations of terms of service have occurred</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>3. Non-Refundable Items</h2>
            <p>The following items are not eligible for refunds:</p>
            <ul>
              <li>Products returned after the 30-day period</li>
              <li>Products damaged due to misuse or improper installation</li>
              <li>Products without original packaging or missing accessories</li>
              <li>Customized or special-order products</li>
              <li>Shipping and handling charges</li>
              <li>Products purchased from unauthorized resellers</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>4. Defective Products</h2>
            <p>
              If you receive a defective product, please contact us immediately. Defective products are eligible for:
            </p>
            <ul>
              <li>Full refund within 30 days of delivery</li>
              <li>Replacement product (subject to availability)</li>
              <li>Repair services covered under warranty</li>
            </ul>
            <p>
              Defective products must be reported within 7 days of receipt to qualify for expedited replacement.
            </p>
          </section>

          <section className="policy-page__section">
            <h2>5. How to Request a Refund</h2>
            <p>To request a refund, please follow these steps:</p>
            <ol>
              <li><strong>Contact Customer Support:</strong> Email us at support@chillverify.com or call 1-800-CHILL-01</li>
              <li><strong>Provide Information:</strong> Include your order number, product details, and reason for refund</li>
              <li><strong>Receive RMA Number:</strong> We will issue a Return Merchandise Authorization (RMA) number</li>
              <li><strong>Package Product:</strong> Securely package the item with all original materials</li>
              <li><strong>Ship Product:</strong> Send the package to the address provided with your RMA number</li>
              <li><strong>Tracking:</strong> Use a trackable shipping method and keep your tracking number</li>
            </ol>
          </section>

          <section className="policy-page__section">
            <h2>6. Refund Processing Time</h2>
            <p>Refunds are typically processed within 5-10 business days after we receive your returned item. The timeline depends on:</p>
            <ul>
              <li>Time to receive and inspect the returned product</li>
              <li>Your original payment method</li>
              <li>Your bank or credit card company's processing time</li>
            </ul>
            <p><strong>Processing Times by Payment Method:</strong></p>
            <ul>
              <li>Credit/Debit Cards: 5-10 business days</li>
              <li>PayPal: 3-5 business days</li>
              <li>Bank Transfer: 7-14 business days</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>7. Return Shipping Costs</h2>
            <p><strong>Customer Responsibility:</strong></p>
            <ul>
              <li>Return shipping costs for non-defective products</li>
              <li>Insurance for returned items (recommended)</li>
              <li>Any customs or import duties for international returns</li>
            </ul>
            <p><strong>ChillVerify Responsibility:</strong></p>
            <ul>
              <li>Return shipping costs for defective products</li>
              <li>Replacement shipping costs for warranty items</li>
              <li>Shipping costs for errors made by ChillVerify</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>8. Refund Methods</h2>
            <p>Refunds are issued using the same payment method used for the original purchase:</p>
            <ul>
              <li><strong>Credit/Debit Cards:</strong> Refunded to the original card</li>
              <li><strong>PayPal:</strong> Refunded to the original PayPal account</li>
              <li><strong>Bank Transfer:</strong> Refunded to the original bank account</li>
              <li><strong>Store Credit:</strong> Available upon request as an alternative</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>9. Partial Refunds</h2>
            <p>Partial refunds may be issued in the following situations:</p>
            <ul>
              <li>Product is returned with missing accessories or packaging</li>
              <li>Product shows signs of use beyond normal inspection</li>
              <li>Original shipping costs are non-refundable</li>
              <li>Restocking fees may apply to certain products (up to 15%)</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>10. Exchange Policy</h2>
            <p>
              Instead of a refund, you may choose to exchange your product for a different model or color. Exchanges follow the same conditions as refunds and must be requested within the 30-day period.
            </p>
            <p>Exchange conditions:</p>
            <ul>
              <li>Price differences will be charged or refunded accordingly</li>
              <li>Exchange shipping costs apply</li>
              <li>Subject to product availability</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>11. Warranty Claims</h2>
            <p>
              Warranty claims are handled separately from refund requests. Please refer to our Warranty Policy for information about product warranties and repair services.
            </p>
          </section>

          <section className="policy-page__section">
            <h2>12. Contact Information</h2>
            <p>For refund inquiries, please contact us:</p>
            <ul>
              <li><strong>Email:</strong> support@chillverify.com</li>
              <li><strong>Phone:</strong> +61 3 6427 1234</li>
              <li><strong>Hours:</strong> Monday-Friday, 9:00 AM - 5:00 PM AEST</li>
              <li><strong>Address:</strong> ChillVerify Returns, 8 Beachrock View, East Devonport, Tasmania 7310, Australia</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>13. Policy Changes</h2>
            <p>
              ChillVerify reserves the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting on our website. Continued use of our products constitutes acceptance of any policy changes.
            </p>
          </section>

          <div className="policy-page__footer">
            <p>
              This Refund Policy is part of our commitment to customer satisfaction and fair business practices. By purchasing from ChillVerify, you agree to the terms outlined in this policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
